<!-- Редактирует основные данные заказа в форме изменения. -->
<template>
  <div class="order-edit-wrapper">
    <!-- Основная информация -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="info" :size="18" />
      <span>Основная информация</span>
    </h3>

    <div class="form-row">
      <!-- Номер заказа -->
      <div class="form-group">
        <OrderNumberField
          v-model="editOrderForm.number"
          :required="true"
          :filled="isFieldFilled('number')"
          :invalid="errors.number"
          error-text="Поле обязательно для заполнения"
          @update:modelValue="errors.number = false"
        />
      </div>

      <div class="form-group">
        <base-label
          for="order_date"
          :required="true"
          :filled="isFieldFilled('order_date')"
        >
          Дата заказа
        </base-label>
        <base-input
          id="order_date"
          v-model="editOrderForm.order_date"
          type="date"
          class="form-input"
          :invalid="errors.order_date"
          :clearable="false"
          @update:modelValue="errors.order_date = false"
        />
        <span class="error-message" v-if="errors.order_date">
          Поле обязательно для заполнения
        </span>
      </div>

      <div class="form-group">
        <base-label for="status">Статус</base-label>
        <base-select
          id="status"
          v-model="editOrderForm.status"
          :options="orderStore.orderStatusSelectOptions"
          :clearable="false"
          placeholder="Выберите статус заказа"
        />
      </div>
    </div>

    <div class="form-row">
      <!-- Тип заказа -->
      <div class="form-group">
        <OrderTypeField v-model="editOrderForm.order_type_id" />
      </div>

      <div class="form-group">
        <base-label for="waybill_id">Путевой лист</base-label>
        <WaybillSelect
          id="waybill_id"
          v-model="editOrderForm.waybill_id"
          placeholder="Выберите путевой лист"
          :with-button="true"
          button-label="Создать путевой лист"
          @button-click="showWaybillForm = true"
        />
      </div>

      <div class="form-group">
        <base-label for="responsible_user_id">
          Ответственный за заказ
        </base-label>
        <CompanyUserSelect
          id="responsible_user_id"
          v-model="editOrderForm.responsible_user_id"
          placeholder="Выберите ответственного"
          :clearable="true"
        />
      </div>
    </div>

    <!-- Данные о грузе -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="cargo" :size="18" />
      <span>Данные о грузе</span>
    </h3>

    <div class="form-row">
      <div class="form-group">
        <base-label
          for="cargo_name"
          :required="true"
          :filled="isFieldFilled('cargo_name')"
          info-key="order.cargo"
        >
          Название груза
        </base-label>
        <base-input
          id="cargo_name"
          v-model="editOrderForm.cargo_name"
          type="text"
          class="form-input"
          :invalid="errors.cargo_name"
          @update:modelValue="errors.cargo_name = false"
        />
        <span class="error-message" v-if="errors.cargo_name">
          Поле обязательно для заполнения
        </span>
      </div>

      <div class="form-group">
        <base-label
          for="cargo_weight"
          :filled="isFieldFilled('cargo_weight')"
          info-key="order.cargo"
        >
          Вес груза (кг)
        </base-label>
        <base-input
          id="cargo_weight"
          v-model.number="editOrderForm.cargo_weight"
          type="big_number"
          step="any"
          min="0"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <base-label
          for="cargo_volume"
          :filled="isFieldFilled('cargo_volume')"
          info-key="order.cargo"
        >
          Объем груза (м³)
        </base-label>
        <base-input
          id="cargo_volume"
          v-model.number="editOrderForm.cargo_volume"
          type="big_number"
          step="any"
          min="0"
          class="form-input"
        />
      </div>
    </div>

    <!-- Цены -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="payment" :size="18" />
      <span>Цены</span>
    </h3>

    <div class="form-row">
      <div class="form-group">
        <base-label
          for="price"
          :required="true"
          :filled="isFieldFilled('price')"
          info-key="order.payment"
        >
          Цена
        </base-label>
        <base-input
          id="price"
          v-model.number="editOrderForm.price"
          type="big_number"
          step="any"
          min="0"
          class="form-input"
          :invalid="errors.price"
          @update:modelValue="errors.price = false"
        />
        <span class="error-message" v-if="errors.price">
          Поле обязательно для заполнения
        </span>
      </div>

      <div class="form-group">
        <base-label
          for="payment_form"
          :required="true"
          :filled="isFieldFilled('payment_form')"
          info-key="order.payment"
        >
          Форма оплаты
        </base-label>
        <base-select
          id="payment_form"
          v-model="editOrderForm.payment_form"
          :options="orderStore.paymentFormOptions"
          placeholder="Выберите форму оплаты"
          :invalid="errors.payment_form"
          :clearable="false"
          @update:modelValue="errors.payment_form = false"
        />
        <span class="error-message" v-if="errors.payment_form">
          Поле обязательно для заполнения
        </span>
      </div>

      <div class="form-group">
        <base-label for="payment_status">Статус оплаты</base-label>
        <base-select
          id="payment_status"
          v-model="editOrderForm.payment_status"
          :options="orderStore.paymentStatusSelectOptions"
          :clearable="false"
          placeholder="Выберите статус оплаты"
        />
      </div>
    </div>

    <!-- Контрагенты -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="participants" :size="18" />
      <span>Контрагенты</span>
    </h3>

    <div class="counterparty-grid">
      <!-- Заказчик -->
      <div class="form-group counterparty-group">
        <base-label
          class="counterparty-label"
          info-key="order.customer"
          :filled="counterpartyRoleIsFilled(customers)"
        >
          Заказчик
        </base-label>

        <div
          v-for="(customer, index) in customers"
          :key="`customer-${index}`"
          class="counterparty-item"
        >
          <div class="counterparty-select">
            <CounterpartySelect
              :model-value="customer.counterparty_id"
              placeholder="Выберите заказчика"
              :with-button="true"
              button-label="Создать нового контрагента"
              :exclude-ids="getExcludeIds('customer', index)"
              @update:model-value="
                (value) =>
                  onCounterpartyChange('customer', index, value)
              "
              @button-click="
                () => openCounterpartyModal('customer', index)
              "
            />
            <BaseDeleteIconButton
              type="button"
              @click="removeCustomer(index)"
              title="Удалить заказчика"
              v-if="
                customers.length > 1 ||
                customer.counterparty_id !== null
              "
            />
          </div>
        </div>

        <base-button
          type="button"
          @click="addCustomer"
          class="add-btn"
          v-if="availableCustomersCount > 0"
        >
          Добавить заказчика
        </base-button>
      </div>

      <!-- Грузоотправитель -->
      <div class="form-group counterparty-group">
        <base-label
          class="counterparty-label"
          info-key="order.shipper"
          :filled="counterpartyRoleIsFilled(shippers)"
        >
          Грузоотправитель
        </base-label>

        <div
          v-for="(shipper, index) in shippers"
          :key="`shipper-${index}`"
          class="counterparty-item"
        >
          <div class="counterparty-select">
            <CounterpartySelect
              :model-value="shipper.counterparty_id"
              placeholder="Выберите грузоотправителя"
              :with-button="true"
              button-label="Создать нового контрагента"
              :exclude-ids="getExcludeIds('shipper', index)"
              @update:model-value="
                (value) =>
                  onCounterpartyChange('shipper', index, value)
              "
              @button-click="
                () => openCounterpartyModal('shipper', index)
              "
            />
            <BaseDeleteIconButton
              type="button"
              @click="removeShipper(index)"
              title="Удалить грузоотправителя"
              v-if="
                shippers.length > 1 ||
                shipper.counterparty_id !== null
              "
            />
          </div>
        </div>

        <base-button
          type="button"
          @click="addShipper"
          class="add-btn"
          v-if="availableShippersCount > 0"
        >
          Добавить грузоотправителя
        </base-button>
      </div>

      <!-- Перевозчик -->
      <div class="form-group counterparty-group">
        <base-label
          class="counterparty-label"
          info-key="order.carrier"
          :filled="counterpartyRoleIsFilled(carriers)"
        >
          Перевозчик
        </base-label>

        <div
          v-for="(carrier, index) in carriers"
          :key="`carrier-${index}`"
          class="counterparty-item"
        >
          <div class="counterparty-select">
            <CounterpartySelect
              :model-value="carrier.counterparty_id"
              placeholder="Выберите перевозчика"
              :with-button="true"
              button-label="Создать нового контрагента"
              :exclude-ids="getExcludeIds('carrier', index)"
              @update:model-value="
                (value) =>
                  onCounterpartyChange('carrier', index, value)
              "
              @button-click="
                () => openCounterpartyModal('carrier', index)
              "
            />
            <BaseDeleteIconButton
              type="button"
              @click="removeCarrier(index)"
              title="Удалить перевозчика"
              v-if="
                carriers.length > 1 ||
                carrier.counterparty_id !== null
              "
            />
          </div>
        </div>

        <base-button
          type="button"
          @click="addCarrier"
          class="add-btn"
          v-if="availableCarriersCount > 0"
        >
          Добавить перевозчика
        </base-button>
      </div>

      <!-- Экспедитор -->
      <div class="form-group counterparty-group">
        <base-label
          class="counterparty-label"
          info-key="order.forwarder"
          :filled="counterpartyRoleIsFilled(forwarders)"
        >
          Экспедитор
        </base-label>
        <p class="counterparty-note">
          Используется для создания поручения экспедитору
        </p>

        <div
          v-for="(forwarder, index) in forwarders"
          :key="`forwarder-${index}`"
          class="counterparty-item"
        >
          <div class="counterparty-select">
            <CounterpartySelect
              :model-value="forwarder.counterparty_id"
              placeholder="Выберите экспедитора"
              :with-button="true"
              button-label="Создать нового контрагента"
              :exclude-ids="getExcludeIds('forwarder', index)"
              @update:model-value="
                (value) =>
                  onCounterpartyChange('forwarder', index, value)
              "
              @button-click="
                () => openCounterpartyModal('forwarder', index)
              "
            />
            <BaseDeleteIconButton
              type="button"
              @click="removeForwarder(index)"
              title="Удалить экспедитора"
              v-if="
                forwarders.length > 1 ||
                forwarder.counterparty_id !== null
              "
            />
          </div>
        </div>

        <base-button
          type="button"
          @click="addForwarder"
          class="add-btn"
          v-if="availableForwardersCount > 0"
        >
          Добавить экспедитора
        </base-button>
      </div>

      <!-- Грузополучатель -->
      <div class="form-group counterparty-group">
        <base-label
          class="counterparty-label"
          info-key="order.consignee"
          :filled="counterpartyRoleIsFilled(consignees)"
        >
          Грузополучатель
        </base-label>

        <div
          v-for="(consignee, index) in consignees"
          :key="`consignee-${index}`"
          class="counterparty-item"
        >
          <div class="counterparty-select">
            <CounterpartySelect
              :model-value="consignee.counterparty_id"
              placeholder="Выберите грузополучателя"
              :with-button="true"
              button-label="Создать нового контрагента"
              :exclude-ids="getExcludeIds('consignee', index)"
              @update:model-value="
                (value) =>
                  onCounterpartyChange('consignee', index, value)
              "
              @button-click="
                () => openCounterpartyModal('consignee', index)
              "
            />
            <BaseDeleteIconButton
              type="button"
              @click="removeConsignee(index)"
              title="Удалить грузополучателя"
              v-if="
                consignees.length > 1 ||
                consignee.counterparty_id !== null
              "
            />
          </div>
        </div>

        <base-button
          type="button"
          @click="addConsignee"
          class="add-btn"
          v-if="availableConsigneesCount > 0"
        >
          Добавить грузополучателя
        </base-button>
      </div>

    </div>

    <!-- Контактные данные -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="contact" :size="18" />
      <span>Контактные данные</span>
    </h3>

    <div class="form-row">
      <div class="form-group">
        <base-label for="sender">Отправитель</base-label>
        <base-input
          id="sender"
          v-model="editOrderForm.sender"
          type="text"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <base-label for="receiver">Получатель</base-label>
        <base-input
          id="receiver"
          v-model="editOrderForm.receiver"
          type="text"
          class="form-input"
        />
      </div>
    </div>

    <!--
    TODO: решить, что делать с deprecated полями планового времени заказа
    Плановые даты сейчас задаются через точки заказа и актуализируются backend

    <h3 class="form-title form-title--with-icon title__opt">
      <AppIcon name="time" :size="18" />
      <span>Планируемое время</span>
    </h3>

    <p class="form-hint">
      <span class="hint-icon">ℹ️</span>
      Поля заполняются автоматически при изменении точек заказа
    </p>

    <div class="form-row">
      <div class="form-group">
        <base-label for="planned_pickup_at">
          Время погрузки
        </base-label>
        <base-input
          id="planned_pickup_at"
          v-model="editOrderForm.planned_pickup_at"
          type="datetime-local"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <base-label for="planned_delivery_at">
          Время доставки
        </base-label>
        <base-input
          id="planned_delivery_at"
          v-model="editOrderForm.planned_delivery_at"
          type="datetime-local"
          class="form-input"
        />
      </div>
    </div>
    -->

    <!-- Фактическое время -->
    <div class="actual-box" v-if="showActualTimeFields">
      <h3 class="form-title form-title--with-icon">
        <AppIcon name="time" :size="18" />
        <span>Фактическое время</span>
      </h3>

      <div class="form-row">
        <div class="form-group">
          <base-label for="actual_pickup_at">
            Время погрузки
          </base-label>
          <base-input
            id="actual_pickup_at"
            v-model="editOrderForm.actual_pickup_at"
            type="datetime-local"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <base-label for="actual_delivery_at">
            Время разгрузки
          </base-label>
          <base-input
            id="actual_delivery_at"
            v-model="editOrderForm.actual_delivery_at"
            type="datetime-local"
            class="form-input"
          />
        </div>
      </div>
    </div>

    <!-- Комментарии -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="comment" :size="18" />
      <span>Комментарии</span>
    </h3>

    <div class="form-group">
      <base-label
        for="comment"
        :filled="isFieldFilled('comment')"
        info-key="order.comment"
      >
        Комментарий
      </base-label>
      <base-textarea
        id="comment"
        v-model="editOrderForm.comment"
        class="form-input"
      />
    </div>

    <!-- Температурные требования -->
    <h3 class="form-title form-title--with-icon">
      <AppIcon name="temperature" :size="18" />
      <span>Температурные требования</span>
    </h3>

    <div class="form-row">
      <div class="form-group">
        <base-label for="min_temperature">
          Мин. температура (°C)
        </base-label>
        <base-input
          id="min_temperature"
          v-model.number="editOrderForm.min_temperature"
          type="number"
          step="any"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <base-label for="max_temperature">
          Макс. температура (°C)
        </base-label>
        <base-input
          id="max_temperature"
          v-model.number="editOrderForm.max_temperature"
          type="number"
          step="any"
          class="form-input"
        />
      </div>
    </div>

    <AddCounterpartyModal
      v-if="isCounterpartyModalVisible"
      @counterparty-created="updateCounterparties"
      @close="isCounterpartyModalVisible = false"
    />

    <WaybillForm
      v-if="showWaybillForm"
      @close="showWaybillForm = false"
      @waybill-created="handleWaybillCreated"
    />

  </div>
</template>

<script>
import AddCounterpartyModal from "@/components/Counterparty/AddCounterpartyModal.vue";
import CounterpartySelect from "@/components/Counterparty/CounterpartySelect.vue";
import OrderNumberField from "@/components/Order/NumberField.vue";
import OrderTypeField from "@/components/Order/TypeField.vue";
import CompanyUserSelect from "@/components/CompanyUser/CompanyUserSelect.vue";
import WaybillSelect from "@/components/Waybill/WaybillSelect.vue";
import WaybillForm from "@/components/Waybill/WaybillForm.vue";
import AppIcon from "@/components/Icon/AppIcon.vue";

import { useNotificationStore } from "@/stores/notificationStore";
import { useOrderStore } from "@/stores/orderStore";

import {
  formatDateAndTime,
  formatDateTimeLocal,
} from "@/utils/dateUtils";

const DEPRECATED_COUNTERPARTY_ROLES = [];

export default {
  name: "OrderEditMainInfo",

  components: {
    AddCounterpartyModal,
    CounterpartySelect,
    OrderNumberField,
    OrderTypeField,
    CompanyUserSelect,
    WaybillSelect,
    WaybillForm,
    AppIcon,
  },

  props: {
    formData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      editOrderForm: { ...this.formData },

      customers: [{ counterparty_id: null, role: "customer" }],
      shippers: [{ counterparty_id: null, role: "shipper" }],
      consignees: [{ counterparty_id: null, role: "consignee" }],
      carriers: [{ counterparty_id: null, role: "carrier" }],
      forwarders: [{ counterparty_id: null, role: "forwarder" }],
      deprecatedCounterparties: [],

      errors: {
        cargo_name: false,
        payment_form: false,
        order_date: false,
        price: false,
        number: false,
      },

      isCounterpartyModalVisible: false,
      showWaybillForm: false,
      counterpartyModalContext: {
        role: null,
        index: null,
      },
    };
  },

  watch: {
    editOrderForm: {
      handler(newVal) {
        this.$emit("update:formData", newVal);
      },
      deep: true,
    },

    formData: {
      handler(newVal) {
        if (
          JSON.stringify(newVal) !==
          JSON.stringify(this.editOrderForm)
        ) {
          this.editOrderForm = { ...newVal };
          this.loadCounterpartiesFromFormData();
        }
      },
      deep: true,
    },
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    orderStore() {
      return useOrderStore();
    },

    showActualTimeFields() {
      return ["completed", "canceled"].includes(
        this.editOrderForm.status
      );
    },

    availableCustomersCount() {
      const usedIds = this.customers
        .map((c) => c.counterparty_id)
        .filter((id) => id !== null);
      return usedIds.length < 10 ? 1 : 0;
    },

    availableCarriersCount() {
      const usedIds = this.carriers
        .map((c) => c.counterparty_id)
        .filter((id) => id !== null);
      return usedIds.length < 10 ? 1 : 0;
    },

    availableForwardersCount() {
      const usedIds = this.forwarders
        .map((c) => c.counterparty_id)
        .filter((id) => id !== null);
      return usedIds.length < 10 ? 1 : 0;
    },

    availableShippersCount() {
      const usedIds = this.shippers
        .map((s) => s.counterparty_id)
        .filter((id) => id !== null);
      return usedIds.length < 10 ? 1 : 0;
    },

    availableConsigneesCount() {
      const usedIds = this.consignees
        .map((c) => c.counterparty_id)
        .filter((id) => id !== null);
      return usedIds.length < 10 ? 1 : 0;
    },

  },

  methods: {
    counterpartyRoleIsFilled(counterparties) {
      return counterparties.some((item) => item.counterparty_id);
    },

    // Возвращает excludeIds для конкретного селекта — все выбранные в этой роли кроме текущего
    getExcludeIds(role, currentIndex) {
      let list = [];
      switch (role) {
        case "customer":
          list = this.customers;
          break;
        case "shipper":
          list = this.shippers;
          break;
        case "consignee":
          list = this.consignees;
          break;
        case "carrier":
          list = this.carriers;
          break;
        case "forwarder":
          list = this.forwarders;
          break;
      }
      return list
        .filter((_, i) => i !== currentIndex)
        .map((item) => item.counterparty_id)
        .filter((id) => id !== null);
    },

    isFieldFilled(field) {
      const value = this.editOrderForm[field];
      return value !== "" && value !== null && value !== undefined;
    },

    loadCounterpartiesFromFormData() {
      if (
        this.formData.order_counterparties &&
        this.formData.order_counterparties.length > 0
      ) {
        this.resetCounterparties();

        const customers = [];
        const shippers = [];
        const consignees = [];
        const carriers = [];
        const forwarders = [];
        const deprecatedCounterparties = [];

        this.formData.order_counterparties.forEach((counterparty) => {
          const item = {
            counterparty_id: counterparty.counterparty_id,
            role: counterparty.role,
          };

          switch (counterparty.role) {
            case "customer":
              customers.push(item);
              break;
            case "shipper":
              shippers.push(item);
              break;
            case "consignee":
              consignees.push(item);
              break;
            case "carrier":
              carriers.push(item);
              break;
            case "forwarder":
              forwarders.push(item);
              break;
            default:
              if (!DEPRECATED_COUNTERPARTY_ROLES.includes(counterparty.role)) break;

              deprecatedCounterparties.push(item);
          }
        });

        this.customers =
          customers.length > 0
            ? customers
            : [{ counterparty_id: null, role: "customer" }];
        this.shippers =
          shippers.length > 0
            ? shippers
            : [{ counterparty_id: null, role: "shipper" }];
        this.consignees =
          consignees.length > 0
            ? consignees
            : [{ counterparty_id: null, role: "consignee" }];
        this.carriers =
          carriers.length > 0
            ? carriers
            : [{ counterparty_id: null, role: "carrier" }];
        this.forwarders =
          forwarders.length > 0
            ? forwarders
            : [{ counterparty_id: null, role: "forwarder" }];
        this.deprecatedCounterparties = deprecatedCounterparties;
      } else {
        this.resetCounterparties();
      }

      this.updateEditOrderForm();
    },

    validate() {
      this.errors = {
        cargo_name: false,
        order_date: false,
        payment_form: false,
        price: false,
        number: false,
      };

      let isValid = true;

      if (!this.editOrderForm.cargo_name) {
        this.errors.cargo_name = true;
        isValid = false;
      }
      if (!this.editOrderForm.order_date) {
        this.errors.order_date = true;
        isValid = false;
      }
      if (!this.editOrderForm.payment_form) {
        this.errors.payment_form = true;
        isValid = false;
      }
      if (!this.editOrderForm.price) {
        this.errors.price = true;
        isValid = false;
      }
      if (!this.editOrderForm.number?.trim()) {
        this.errors.number = true;
        isValid = false;
      }

      if (!isValid) {
        this.$nextTick(() => {
          const firstError = this.$el.querySelector(
            ".invalid, .error-message"
          );
          if (firstError) {
            const formGroup = firstError.closest(".form-group");
            (formGroup || firstError).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
      }

      return isValid;
    },

    addCustomer() {
      if (this.availableCustomersCount > 0) {
        this.customers.push({
          counterparty_id: null,
          role: "customer",
        });
      }
    },

    removeCustomer(index) {
      if (
        this.customers.length === 1 &&
        this.customers[0].counterparty_id === null
      )
        return;
      this.customers.splice(index, 1);
      this.updateEditOrderForm();
    },

    addCarrier() {
      if (this.availableCarriersCount > 0) {
        this.carriers.push({
          counterparty_id: null,
          role: "carrier",
        });
      }
    },

    removeCarrier(index) {
      if (
        this.carriers.length === 1 &&
        this.carriers[0].counterparty_id === null
      )
        return;
      this.carriers.splice(index, 1);
      this.updateEditOrderForm();
    },

    addForwarder() {
      if (this.availableForwardersCount > 0) {
        this.forwarders.push({
          counterparty_id: null,
          role: "forwarder",
        });
      }
    },

    removeForwarder(index) {
      if (
        this.forwarders.length === 1 &&
        this.forwarders[0].counterparty_id === null
      )
        return;
      this.forwarders.splice(index, 1);
      this.updateEditOrderForm();
    },

    addShipper() {
      if (this.availableShippersCount > 0) {
        this.shippers.push({
          counterparty_id: null,
          role: "shipper",
        });
      }
    },

    removeShipper(index) {
      if (
        this.shippers.length === 1 &&
        this.shippers[0].counterparty_id === null
      )
        return;
      this.shippers.splice(index, 1);
      this.updateEditOrderForm();
    },

    addConsignee() {
      if (this.availableConsigneesCount > 0) {
        this.consignees.push({
          counterparty_id: null,
          role: "consignee",
        });
      }
    },

    removeConsignee(index) {
      if (
        this.consignees.length === 1 &&
        this.consignees[0].counterparty_id === null
      )
        return;
      this.consignees.splice(index, 1);
      this.updateEditOrderForm();
    },

    resetCounterparties() {
      this.customers = [{ counterparty_id: null, role: "customer" }];
      this.shippers = [{ counterparty_id: null, role: "shipper" }];
      this.consignees = [
        { counterparty_id: null, role: "consignee" },
      ];
      this.carriers = [{ counterparty_id: null, role: "carrier" }];
      this.forwarders = [{ counterparty_id: null, role: "forwarder" }];
      this.deprecatedCounterparties = [];
    },

    updateEditOrderForm() {
      this.$nextTick(() => {
        this.editOrderForm.order_counterparties =
          this.getAllCounterparties();
        this.$emit("update:formData", this.editOrderForm);
      });
    },

    onCounterpartyChange(role, index, value) {
      switch (role) {
        case "customer":
          if (this.customers[index]) {
            this.customers[index].counterparty_id = value;
          }
          break;
        case "shipper":
          if (this.shippers[index]) {
            this.shippers[index].counterparty_id = value;
          }
          break;
        case "consignee":
          if (this.consignees[index]) {
            this.consignees[index].counterparty_id = value;
          }
          break;
        case "carrier":
          if (this.carriers[index]) {
            this.carriers[index].counterparty_id = value;
          }
          break;
        case "forwarder":
          if (this.forwarders[index]) {
            this.forwarders[index].counterparty_id = value;
          }
          break;
      }
      this.updateEditOrderForm();
    },

    getAllCounterparties() {
      const allCounterparties = [];

      this.customers.forEach((customer) => {
        if (customer.counterparty_id !== null) {
          allCounterparties.push({
            role: "customer",
            counterparty_id: customer.counterparty_id,
          });
        }
      });

      this.shippers.forEach((shipper) => {
        if (shipper.counterparty_id !== null) {
          allCounterparties.push({
            role: "shipper",
            counterparty_id: shipper.counterparty_id,
          });
        }
      });

      this.consignees.forEach((consignee) => {
        if (consignee.counterparty_id !== null) {
          allCounterparties.push({
            role: "consignee",
            counterparty_id: consignee.counterparty_id,
          });
        }
      });

      this.carriers.forEach((carrier) => {
        if (carrier.counterparty_id !== null) {
          allCounterparties.push({
            role: "carrier",
            counterparty_id: carrier.counterparty_id,
          });
        }
      });

      this.forwarders.forEach((forwarder) => {
        if (forwarder.counterparty_id !== null) {
          allCounterparties.push({
            role: "forwarder",
            counterparty_id: forwarder.counterparty_id,
          });
        }
      });

      return allCounterparties.concat(this.deprecatedCounterparties);
    },

    updateCounterparties(newCounterparty) {
      if (
        this.counterpartyModalContext.role &&
        this.counterpartyModalContext.index !== null
      ) {
        const { role, index } = this.counterpartyModalContext;

        switch (role) {
          case "customer":
            if (this.customers[index]) {
              this.customers[index].counterparty_id =
                newCounterparty.id;
            }
            break;
          case "shipper":
            if (this.shippers[index]) {
              this.shippers[index].counterparty_id =
                newCounterparty.id;
            }
            break;
          case "consignee":
            if (this.consignees[index]) {
              this.consignees[index].counterparty_id =
                newCounterparty.id;
            }
            break;
          case "carrier":
            if (this.carriers[index]) {
              this.carriers[index].counterparty_id =
                newCounterparty.id;
            }
            break;
          case "forwarder":
            if (this.forwarders[index]) {
              this.forwarders[index].counterparty_id =
                newCounterparty.id;
            }
            break;
        }

        this.counterpartyModalContext = { role: null, index: null };
        this.updateEditOrderForm();
      }
    },

    openCounterpartyModal(role, index) {
      this.counterpartyModalContext = { role, index };
      this.isCounterpartyModalVisible = true;
    },

    handleWaybillCreated(waybill) {
      this.editOrderForm.waybill_id = waybill.id;
      this.showWaybillForm = false;
    },

    formatDateAndTime,
    formatDateTimeLocal,
  },

  created() {
    this.loadCounterpartiesFromFormData();
  },
};
</script>

<style scoped>
.form-title {
  margin-bottom: var(--base-in-indent);
  margin-top: var(--base-in-indent);
}

.form-title--with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title__opt {
  margin-bottom: 10px;
}

.form-hint {
  margin-bottom: var(--base-in-indent);
  font-size: 14px;
  color: #888;
  font-style: italic;
}

.hint-icon {
  font-style: normal;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0 20px;
  align-items: start;
}

.counterparty-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
  align-items: start;
}

.actual-box {
  margin-bottom: 20px;
}

.counterparty-group {
  min-width: 0;
}

.counterparty-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.counterparty-note {
  margin: -4px 0 10px;
  color: #64748b;
  font-size: 13px;
  line-height: 18px;
}

.counterparty-item {
  min-width: 0;
  margin-bottom: 10px;
}

.counterparty-select {
  display: flex;
  gap: 15px;
  align-items: center;
}

.add-btn.add-btn {
  background-color: var(--color-blue);
  color: #fff;
  margin-top: 10px;
}

.add-btn.add-btn:hover {
  background-color: var(--color-blue-hover);
}

.error-message {
  color: var(--color-danger);
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.form-group {
  margin-bottom: 20px;
}

@media screen and (max-width: 1023px) {
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 767px) {
  .form-row,
  .counterparty-grid {
    grid-template-columns: 1fr;
  }
}
</style>
